import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  PlusCircle,
  RefreshCw,
  Search,
  StickyNote,
  Ticket,
  X,
  XCircle,
} from "lucide-react";
import { CSPUTILS } from "../config/API";

const inputClassName =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20";

const textareaClassName =
  "w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20";

const PageShell = ({ children }) => (
  <div className="max-w-6xl mx-auto">
    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
      {children}
    </div>
  </div>
);

const PageHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-5 py-4 sm:px-6">
    <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
        <div>
          <h1 className="text-lg font-bold tracking-tight text-gray-900 sm:text-xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-0.5 max-w-2xl text-xs text-gray-500 sm:text-sm">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {action}
    </div>
  </div>
);

const LoadingSkeleton = () => (
  <PageShell>
    <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 animate-pulse rounded-lg bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-64 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    </div>
    <div className="p-6 sm:p-8 space-y-3">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-xl border border-gray-100 bg-gray-50"
        />
      ))}
    </div>
  </PageShell>
);

const ErrorState = ({ message, onRetry }) => (
  <PageShell>
    <div className="flex flex-col items-center px-6 py-16 text-center sm:px-8">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
        <AlertCircle className="h-7 w-7" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">Unable to load tickets</h2>
      <p className="mt-2 max-w-md text-sm text-gray-600">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90"
        >
          Try Again
        </button>
      )}
    </div>
  </PageShell>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-14 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-400">
      <Icon className="h-7 w-7" />
    </div>
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";

  const datePart = String(dateStr).trim().split(/\s+/)[0];
  if (!datePart) return "—";

  const toDisplayDate = (year, month, day) => {
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(date.getTime())) return null;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${date.getFullYear()}`;
  };

  const ddMmYyyy = datePart.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (ddMmYyyy) {
    const formatted = toDisplayDate(ddMmYyyy[3], ddMmYyyy[2], ddMmYyyy[1]);
    if (formatted) return formatted;
  }

  const mmDdYyyy = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmDdYyyy) {
    const formatted = toDisplayDate(mmDdYyyy[3], mmDdYyyy[1], mmDdYyyy[2]);
    if (formatted) return formatted;
  }

  const yyyyMmDd = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (yyyyMmDd) {
    const formatted = toDisplayDate(yyyyMmDd[1], yyyyMmDd[2], yyyyMmDd[3]);
    if (formatted) return formatted;
  }

  return datePart;
};

const formatTicketNumber = (ticketNumber) => {
  if (ticketNumber === null || ticketNumber === undefined || ticketNumber === "") {
    return "";
  }

  const str = String(ticketNumber).trim();
  const dotIndex = str.indexOf(".");
  return dotIndex === -1 ? str : str.slice(0, dotIndex);
};

const getDateSortValue = (dateStr) => {
  if (!dateStr) return null;

  const datePart = String(dateStr).trim().split(/\s+/)[0];
  if (!datePart) return null;

  const toTimestamp = (year, month, day) => {
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  };

  const ddMmYyyy = datePart.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (ddMmYyyy) return toTimestamp(ddMmYyyy[3], ddMmYyyy[2], ddMmYyyy[1]);

  const mmDdYyyy = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmDdYyyy) return toTimestamp(mmDdYyyy[3], mmDdYyyy[1], mmDdYyyy[2]);

  const yyyyMmDd = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (yyyyMmDd) return toTimestamp(yyyyMmDd[1], yyyyMmDd[2], yyyyMmDd[3]);

  const parsed = Date.parse(datePart);
  return Number.isNaN(parsed) ? null : parsed;
};

const getTicketNumberSortValue = (ticketNumber) => {
  const digits = String(formatTicketNumber(ticketNumber)).replace(/[^\d]/g, "");
  if (!digits) return null;
  const num = Number(digits);
  return Number.isNaN(num) ? null : num;
};

const SORT_ACCESSORS = {
  ticketnumber: (ticket) => ({ value: getTicketNumberSortValue(ticket.ticketnumber), type: "number" }),
  supporttype: (ticket) => ({ value: ticket.supporttype, type: "string" }),
  dateopened: (ticket) => ({ value: getDateSortValue(ticket.dateopened), type: "number" }),
  issuedescription: (ticket) => ({ value: ticket.issuedescription, type: "string" }),
  status: (ticket) => ({ value: ticket.status, type: "string" }),
};

const compareByKey = (a, b, key, direction) => {
  const accessor = SORT_ACCESSORS[key];
  if (!accessor) return 0;

  const { value: rawA, type } = accessor(a);
  const { value: rawB } = accessor(b);

  const aEmpty = rawA === null || rawA === undefined || rawA === "";
  const bEmpty = rawB === null || rawB === undefined || rawB === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result;
  if (type === "number") {
    result = rawA - rawB;
  } else {
    result = String(rawA).localeCompare(String(rawB), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  return direction === "asc" ? result : -result;
};

const compareTickets = (a, b, key, direction) => {
  const primary = compareByKey(a, b, key, direction);
  if (primary !== 0 || key === "ticketnumber") return primary;

  // Stable tiebreaker so equal values keep a deterministic order.
  const tieA = getTicketNumberSortValue(a.ticketnumber);
  const tieB = getTicketNumberSortValue(b.ticketnumber);
  if (tieA === null && tieB === null) return 0;
  if (tieA === null) return 1;
  if (tieB === null) return -1;
  return tieA - tieB;
};

const getStatusStyles = (status) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "open") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }
  if (normalized === "closed" || normalized === "resolved") {
    return "bg-gray-100 text-gray-600 ring-gray-500/10";
  }
  if (normalized === "pending" || normalized === "in progress") {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  }
  return "bg-primary/10 text-primary ring-primary/20";
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusStyles(status)}`}
  >
    {status || "Unknown"}
  </span>
);

const getDispositionStyles = (disposition) => {
  const normalized = (disposition || "").toLowerCase().trim();

  if (normalized === "n/a" || normalized === "na") {
    return "bg-gray-100 text-gray-600 ring-gray-500/10";
  }
  if (normalized === "pending") {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  }
  if (normalized === "in process") {
    return "bg-blue-50 text-blue-700 ring-blue-600/20";
  }
  if (normalized === "ready for review") {
    return "bg-orange-50 text-orange-700 ring-orange-600/20";
  }
  if (normalized === "complete") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }
  return "bg-gray-100 text-gray-600 ring-gray-500/10";
};

const DispositionBadge = ({ disposition }) => {
  if (!disposition) return <span className="text-sm text-gray-900">—</span>;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getDispositionStyles(disposition)}`}
    >
      {disposition}
    </span>
  );
};

const formatMultilineText = (value) => {
  if (value === null || value === undefined || value === "") return "";

  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatPreviewText = (value) =>
  formatMultilineText(value).replace(/\s*\n\s*/g, " ").trim();

const DetailField = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">
      {formatMultilineText(value) || "—"}
    </dd>
  </div>
);

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getNoteTitle = (note) => note?.title ?? "";
const getNoteDescription = (note) => note?.description ?? "";
const getNoteCreatedBy = (note) => note?.createdby ?? "";
const getNoteDate = (note) => {
  const date = note?.createddate ?? "";
  return date ? formatDate(date) : "";
};

const getFileName = (file) =>
  file?.filename ??
  file?.name ??
  file?.file ??
  file?.title ??
  "File";

const getFileUrl = (file) =>
  file?.filelocation ??
  file?.url ??
  file?.link ??
  file?.filepath ??
  file?.filelink ??
  file?.downloadlink ??
  "";

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Fetch failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const TicketModal = ({ modal, onClose }) => {
  if (!modal) return null;

  const { type, ticket, loading, data, error } = modal;
  const title =
    type === "notes"
      ? `Notes — Ticket #${formatTicketNumber(ticket.ticketnumber)}`
      : `Files — Ticket #${formatTicketNumber(ticket.ticketnumber)}`;
  const Icon = type === "notes" ? StickyNote : FileText;
  const items = normalizeList(data);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {loading && (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <EmptyState
              icon={Icon}
              title={type === "notes" ? "No notes found" : "No files found"}
              description={
                type === "notes"
                  ? "There are no notes attached to this ticket yet."
                  : "There are no files attached to this ticket yet."
              }
            />
          )}

          {!loading && !error && type === "notes" && items.length > 0 && (
            <ul className="space-y-3">
              {items.map((note, index) => {
                const title = getNoteTitle(note);
                const description = getNoteDescription(note);
                const createdBy = getNoteCreatedBy(note);
                const date = getNoteDate(note);

                return (
                  <li
                    key={note?.itemid ?? note?.id ?? index}
                    className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3"
                  >
                    {(date || createdBy) && (
                      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        {date && <span>{date}</span>}
                        {date && createdBy && <span aria-hidden="true">·</span>}
                        {createdBy && <span>By {createdBy}</span>}
                      </div>
                    )}
                    {title && (
                      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    )}
                    {description && (
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
                        {description}
                      </p>
                    )}
                    {!title && !description && (
                      <p className="text-sm text-gray-500">—</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && !error && type === "files" && items.length > 0 && (
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
              {items.map((file, index) => {
                const name = getFileName(file);
                const url = getFileUrl(file);

                return (
                  <li key={file?.itemid ?? file?.id ?? index}>
                    {url ? (
                      <button
                        type="button"
                        onClick={() => downloadFile(url, name)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-primary/5"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <FileText className="h-5 w-5 shrink-0 text-primary" />
                          <span className="truncate text-sm font-medium text-gray-900">
                            {name}
                          </span>
                        </div>
                        <Download className="h-4 w-4 shrink-0 text-gray-400" />
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <FileText className="h-5 w-5 shrink-0 text-gray-400" />
                        <span className="text-sm text-gray-700">{name}</span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const AddNoteModal = ({ ticket, onClose, onSubmit, submitting, error }) => {
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState("");

  if (!ticket) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setValidationError("Note description is required.");
      return;
    }

    setValidationError("");
    onSubmit({ description: trimmedDescription });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-note-title"
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <PlusCircle className="h-4 w-4" />
            </div>
            <h2 id="add-note-title" className="text-lg font-semibold text-gray-900">
              Add Note — Ticket #{formatTicketNumber(ticket.ticketnumber)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-60"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          {(validationError || error) && (
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {validationError || error}
            </div>
          )}

          <div>
            <label
              htmlFor="note-description"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="note-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter note description"
              rows={4}
              disabled={submitting}
              className={textareaClassName}
            />
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <PlusCircle className={`h-4 w-4 ${submitting ? "animate-pulse" : ""}`} />
              {submitting ? "Adding..." : "Add Note"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TicketDetailPanel = ({
  ticket,
  onCloseTicket,
  onViewNotes,
  onViewFiles,
  onAddNote,
  closingId,
  loadingAction,
}) => {
  const isOpen = (ticket.status || "").toLowerCase() === "open";
  const isClosing = closingId === ticket.itemid;
  const isLoadingNotes = loadingAction?.type === "notes" && loadingAction?.itemid === ticket.itemid;
  const isLoadingFiles = loadingAction?.type === "files" && loadingAction?.itemid === ticket.itemid;
  const isAddingNote = loadingAction?.type === "addnote" && loadingAction?.itemid === ticket.itemid;

  return (
  <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-5 sm:px-6">
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <DetailField label="Reporting Party" value={ticket.reportingparty} />
      <DetailField label="Module" value={ticket.module} />
      <DetailField label="Support Technician" value={ticket.supporttechnician} />
      <DetailField label="Submitted By" value={ticket.submittedby} />
      <DetailField label="Status Date" value={formatDate(ticket.statusdate)} />
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Disposition
        </dt>
        <dd className="mt-1">
          <DispositionBadge disposition={ticket.disposition} />
        </dd>
      </div>
      <div className="sm:col-span-2 lg:col-span-3">
        <DetailField label="Issue Description" value={ticket.issuedescription} />
      </div>
      {ticket.solutiondescription && (
        <div className="sm:col-span-2 lg:col-span-3">
          <DetailField label="Solution Description" value={ticket.solutiondescription} />
        </div>
      )}
      {ticket.techcomments && (
        <div className="sm:col-span-2 lg:col-span-3">
          <DetailField label="Tech Comments" value={ticket.techcomments} />
        </div>
      )}
    </dl>
    <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
      <button
        type="button"
        onClick={() => onAddNote(ticket)}
        disabled={isLoadingNotes || isLoadingFiles || isClosing || isAddingNote}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <PlusCircle className={`h-4 w-4 ${isAddingNote ? "animate-pulse" : ""}`} />
        Add Note
      </button>
      <button
        type="button"
        onClick={() => onViewNotes(ticket)}
        disabled={isLoadingNotes || isLoadingFiles || isClosing || isAddingNote}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <StickyNote className={`h-4 w-4 ${isLoadingNotes ? "animate-pulse" : ""}`} />
        {isLoadingNotes ? "Loading..." : "View Notes"}
      </button>
      <button
        type="button"
        onClick={() => onViewFiles(ticket)}
        disabled={isLoadingNotes || isLoadingFiles || isClosing || isAddingNote}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FileText className={`h-4 w-4 ${isLoadingFiles ? "animate-pulse" : ""}`} />
        {isLoadingFiles ? "Loading..." : "View Files"}
      </button>
      {isOpen && onCloseTicket && (
        <button
          type="button"
          onClick={() => onCloseTicket(ticket)}
          disabled={isClosing || isLoadingNotes || isLoadingFiles || isAddingNote}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <XCircle className={`h-4 w-4 ${isClosing ? "animate-pulse" : ""}`} />
          {isClosing ? "Closing..." : "Close Ticket"}
        </button>
      )}
    </div>
  </div>
  );
};

const PAGE_CONFIG = {
  myopen: {
    title: "My Open Tickets",
    subtitle: "View and track your open support tickets submitted to the TribeVue Support Portal.",
    emptyTitle: "No open tickets",
    emptyDescription: "You don't have any open support tickets at the moment.",
  },
  allmy: {
    title: "All My Tickets",
    subtitle: "View all support tickets you have submitted to the TribeVue Support Portal.",
    emptyTitle: "No tickets",
    emptyDescription: "You don't have any support tickets yet.",
  },
  allopen: {
    title: "All Open Tickets",
    subtitle: "View all currently open support tickets in the TribeVue Support Portal.",
    emptyTitle: "No open tickets",
    emptyDescription: "There are no open support tickets at the moment.",
  },
  alltickets: {
    title: "All Tickets",
    subtitle: "View all support tickets in the TribeVue Support Portal.",
    emptyTitle: "No tickets",
    emptyDescription: "There are no support tickets to display.",
  },
};

const getPageConfig = (itype) =>
  PAGE_CONFIG[itype?.toLowerCase()] ?? PAGE_CONFIG.myopen;

const SortableHeader = ({ label, sortKey, sortConfig, onSort, align = "left" }) => {
  const isActive = sortConfig.key === sortKey;
  const SortIcon = !isActive ? ArrowUpDown : sortConfig.direction === "asc" ? ArrowUp : ArrowDown;

  return (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}`}
        className={`group inline-flex items-center gap-1.5 rounded transition hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${align === "right" ? "flex-row-reverse" : ""} ${isActive ? "text-primary" : ""}`}
      >
        <span>{label}</span>
        <SortIcon
          className={`h-3.5 w-3.5 shrink-0 transition ${isActive ? "text-primary" : "text-gray-400 opacity-0 group-hover:opacity-100"}`}
        />
      </button>
    </th>
  );
};

const Tickets = ({ itypeFromUrl }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedId, setExpandedId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [modal, setModal] = useState(null);
  const [addNoteTicket, setAddNoteTicket] = useState(null);
  const [addNoteError, setAddNoteError] = useState(null);
  const [submittingNote, setSubmittingNote] = useState(false);

  const fetchTicketAction = async (action, itemid, extraParams = {}) => {
    const response = await fetch(CSPUTILS, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        action,
        itemid: String(itemid),
        ...extraParams,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (data?.status === "error") {
      throw new Error(data.message || `Failed to load ${action}`);
    }

    return data;
  };

  const fetchTickets = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`${CSPUTILS}?action=${encodeURIComponent(itypeFromUrl)}`, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setTickets(data);
      } else if (data?.status === "error") {
        throw new Error(data.message || "Failed to load tickets");
      } else {
        setTickets([]);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [itypeFromUrl]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const pageConfig = useMemo(
    () => getPageConfig(itypeFromUrl),
    [itypeFromUrl]
  );

  const filteredTickets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return tickets;

    return tickets.filter((ticket) => {
      const fields = [
        formatTicketNumber(ticket.ticketnumber),
        ticket.supporttype,
        ticket.status,
        ticket.reportingparty,
        ticket.module,
        ticket.issuedescription,
      ];
      return fields.some((field) => (field || "").toLowerCase().includes(query));
    });
  }, [tickets, searchQuery]);

  const sortedTickets = useMemo(() => {
    if (!sortConfig.key) return filteredTickets;
    return [...filteredTickets].sort((a, b) =>
      compareTickets(a, b, sortConfig.key, sortConfig.direction)
    );
  }, [filteredTickets, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key !== key) {
        // Dates and numbers default to newest/largest first; text defaults to A→Z.
        const defaultDirection =
          key === "dateopened" || key === "ticketnumber" ? "desc" : "asc";
        return { key, direction: defaultDirection };
      }
      return { key, direction: current.direction === "asc" ? "desc" : "asc" };
    });
  };

  const toggleExpanded = (itemId) => {
    setExpandedId((current) => (current === itemId ? null : itemId));
  };

  const handleCloseTicket = async (ticket) => {
    const confirmed = window.confirm(
      `Close ticket #${formatTicketNumber(ticket.ticketnumber)}? This action cannot be undone.`
    );
    if (!confirmed) return;

    setClosingId(ticket.itemid);

    try {
      await fetchTicketAction("close", ticket.itemid);
      setExpandedId(null);
      setModal(null);
      await fetchTickets(true);
    } catch (err) {
      console.error("Error closing ticket:", err);
      alert(err.message || "Failed to close ticket. Please try again.");
    } finally {
      setClosingId(null);
    }
  };

  const handleViewTicketData = async (ticket, type) => {
    const action = type === "notes" ? "viewnotes" : "viewfiles";

    setLoadingAction({ type, itemid: ticket.itemid });
    setModal({ type, ticket, loading: true, data: null, error: null });
    
    try {
      const data = await fetchTicketAction(action, ticket.itemid);
      setModal({ type, ticket, loading: false, data, error: null });
    } catch (err) {
      console.error(`Error fetching ${action}:`, err);
      setModal({
        type,
        ticket,
        loading: false,
        data: null,
        error: err.message || `Failed to load ${type}.`,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  const handleViewNotes = (ticket) => handleViewTicketData(ticket, "notes");
  const handleViewFiles = (ticket) => handleViewTicketData(ticket, "files");

  const handleAddNote = (ticket) => {
    setAddNoteError(null);
    setAddNoteTicket(ticket);
  };

  const closeAddNoteModal = () => {
    if (submittingNote) return;
    setAddNoteTicket(null);
    setAddNoteError(null);
  };

  const handleSubmitNote = async ({ description }) => {
    if (!addNoteTicket) return;

    setSubmittingNote(true);
    setAddNoteError(null);
    setLoadingAction({ type: "addnote", itemid: addNoteTicket.itemid });

    try {
      await fetchTicketAction("addnote", addNoteTicket.itemid, {
        description,
      });

      setAddNoteTicket(null);

      if (modal?.type === "notes" && modal?.ticket?.itemid === addNoteTicket.itemid) {
        await handleViewTicketData(addNoteTicket, "notes");
      }
    } catch (err) {
      console.error("Error adding note:", err);
      setAddNoteError(err.message || "Failed to add note. Please try again.");
    } finally {
      setSubmittingNote(false);
      setLoadingAction(null);
    }
  };

  const closeModal = () => setModal(null);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => fetchTickets()} />;
  }

  return (
    <PageShell>
      <PageHeader
        icon={Ticket}
        title={pageConfig.title}
        subtitle={pageConfig.subtitle}
        action={
          <button
            onClick={() => fetchTickets(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        }
      />

      <div className="p-5 sm:p-6">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by ticket #, type, status, or description..."
              className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 md:hidden">
              <label htmlFor="mobile-sort" className="sr-only">
                Sort tickets
              </label>
              <select
                id="mobile-sort"
                value={sortConfig.key ? `${sortConfig.key}:${sortConfig.direction}` : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setSortConfig({ key: null, direction: "asc" });
                    return;
                  }
                  const [key, direction] = value.split(":");
                  setSortConfig({ key, direction });
                }}
                className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Sort: Default</option>
                <option value="ticketnumber:asc">Ticket # (Asc)</option>
                <option value="ticketnumber:desc">Ticket # (Desc)</option>
                <option value="supporttype:asc">Support Type (A–Z)</option>
                <option value="supporttype:desc">Support Type (Z–A)</option>
                <option value="dateopened:asc">Date Opened (Oldest)</option>
                <option value="dateopened:desc">Date Opened (Newest)</option>
                <option value="issuedescription:asc">Issue (A–Z)</option>
                <option value="issuedescription:desc">Issue (Z–A)</option>
                <option value="status:asc">Status (A–Z)</option>
                <option value="status:desc">Status (Z–A)</option>
              </select>
            </div>
            <p className="text-sm text-gray-500">
              {filteredTickets.length} ticket{filteredTickets.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {filteredTickets.length === 0 ? (
          <EmptyState
            icon={Ticket}
            title={searchQuery ? "No matching tickets" : pageConfig.emptyTitle}
            description={
              searchQuery
                ? "Try adjusting your search terms to find what you're looking for."
                : pageConfig.emptyDescription
            }
          />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-xl border border-gray-200 md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader
                      label="Ticket #"
                      sortKey="ticketnumber"
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Support Type"
                      sortKey="supporttype"
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Date Opened"
                      sortKey="dateopened"
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Issue"
                      sortKey="issuedescription"
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Status"
                      sortKey="status"
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sortedTickets.map((ticket) => {
                    const isExpanded = expandedId === ticket.itemid;

                    return (
                      <React.Fragment key={ticket.itemid}>
                        <tr className="transition-colors hover:bg-gray-50/80">
                          <td className="whitespace-nowrap px-4 py-4 text-sm font-semibold text-gray-900">
                            #{formatTicketNumber(ticket.ticketnumber)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {ticket.supporttype}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                            {formatDate(ticket.dateopened)}
                          </td>
                          <td className="max-w-xs truncate px-4 py-4 text-sm text-gray-600">
                            {formatPreviewText(ticket.issuedescription) || "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4">
                            <StatusBadge status={ticket.status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-right">
                            <button
                              onClick={() => toggleExpanded(ticket.itemid)}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary transition hover:bg-primary/10"
                            >
                              {isExpanded ? (
                                <>
                                  Hide
                                  <ChevronUp className="h-4 w-4" />
                                </>
                              ) : (
                                <>
                                  View
                                  <ChevronDown className="h-4 w-4" />
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <TicketDetailPanel
                                ticket={ticket}
                                onCloseTicket={handleCloseTicket}
                                onViewNotes={handleViewNotes}
                                onViewFiles={handleViewFiles}
                                onAddNote={handleAddNote}
                                closingId={closingId}
                                loadingAction={loadingAction}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {sortedTickets.map((ticket) => {
                const isExpanded = expandedId === ticket.itemid;

                return (
                  <div
                    key={ticket.itemid}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                  >
                    <button
                      onClick={() => toggleExpanded(ticket.itemid)}
                      className="w-full px-4 py-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              Ticket #{formatTicketNumber(ticket.ticketnumber)}
                            </span>
                            <StatusBadge status={ticket.status} />
                          </div>
                          <p className="mt-1 text-sm text-gray-700">{ticket.supporttype}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Opened {formatDate(ticket.dateopened)}
                          </p>
                          {ticket.issuedescription && (
                            <p className="mt-2 line-clamp-2 text-sm text-gray-600">
                              {formatPreviewText(ticket.issuedescription)}
                            </p>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 shrink-0 text-primary" />
                        ) : (
                          <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <TicketDetailPanel
                        ticket={ticket}
                        onCloseTicket={handleCloseTicket}
                        onViewNotes={handleViewNotes}
                        onViewFiles={handleViewFiles}
                        onAddNote={handleAddNote}
                        closingId={closingId}
                        loadingAction={loadingAction}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <TicketModal modal={modal} onClose={closeModal} />
      <AddNoteModal
        ticket={addNoteTicket}
        onClose={closeAddNoteModal}
        onSubmit={handleSubmitNote}
        submitting={submittingNote}
        error={addNoteError}
      />
    </PageShell>
  );
};

export default Tickets;
